import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as productApi from '../../../shared/api/products.js'
import type { ProductImageItem } from '../../../shared/api/products.js'

interface Props {
  organizationId: string
  productId: string | null
  disabled: boolean
}

export default function ProductImageGallery({ organizationId, productId, disabled }: Props) {
  const { t } = useTranslation()
  const [images, setImages] = useState<ProductImageItem[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!productId) { setLoading(false); return }
    setLoading(true)
    productApi.getProductImages(organizationId, productId)
      .then(setImages)
      .catch(() => setError(t('products.imagesLoadError')))
      .finally(() => setLoading(false))
  }, [productId])

  async function handleUpload(files: FileList | null) {
    if (!files || files.length === 0 || !productId || disabled) return
    setUploading(true)
    setError(null)
    try {
      const result = await productApi.uploadProductImages(organizationId, productId, Array.from(files))
      if (result.errors.length > 0) {
        setError(result.errors.map(e => `${e.filename}: ${e.error}`).join('; '))
      }
      if (result.uploaded.length > 0) {
        // Create DB records for each uploaded file
        const created = await Promise.all(
          result.uploaded.map((u, i) =>
            productApi.createProductImage(organizationId, productId, {
              url: u.url,
              sortOrder: images.length + i,
              isPrimary: images.length === 0 && i === 0,
            }),
          ),
        )
        setImages(prev => [...prev, ...created])
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('products.imagesUploadError'))
    } finally {
      setUploading(false)
    }
  }

  async function handleSetPrimary(imageId: string) {
    if (!productId || disabled) return
    try {
      await productApi.updateProductImage(organizationId, productId, imageId, { isPrimary: true })
      setImages(prev => prev.map(img => ({ ...img, isPrimary: img.id === imageId })))
    } catch {
      setError(t('products.imagesUploadError'))
    }
  }

  async function handleDelete(imageId: string) {
    if (!productId || disabled) return
    if (!window.confirm(t('products.imageDeleteConfirm'))) return
    try {
      await productApi.deleteProductImage(organizationId, productId, imageId)
      const deleted = images.find(i => i.id === imageId)
      setImages(prev => {
        const next = prev.filter(i => i.id !== imageId)
        if (deleted?.isPrimary && next.length > 0) {
          // Auto-assign primary to first remaining
          void productApi.updateProductImage(organizationId, productId, next[0]!.id, { isPrimary: true })
          next[0]!.isPrimary = true
        }
        return next
      })
    } catch {
      setError(t('products.imagesUploadError'))
    }
  }

  async function handleReorder(direction: 'up' | 'down', index: number) {
    if (!productId || disabled) return
    const swapIndex = direction === 'up' ? index - 1 : index + 1
    if (swapIndex < 0 || swapIndex >= images.length) return

    const next = [...images]
    const a = next[index]!
    const b = next[swapIndex]!
    ;[next[index], next[swapIndex]] = [{ ...b, sortOrder: a.sortOrder }, { ...a, sortOrder: b.sortOrder }]
    setImages(next)

    try {
      await Promise.all([
        productApi.updateProductImage(organizationId, productId, a.id, { sortOrder: b.sortOrder }),
        productApi.updateProductImage(organizationId, productId, b.id, { sortOrder: a.sortOrder }),
      ])
    } catch {
      setImages(images) // revert
    }
  }

  if (!productId) return null

  return (
    <div className="product-image-gallery">
      <h3>{t('products.images')}</h3>

      {error && <div className="form-error-banner">{error}</div>}

      {loading && <div className="spinner" />}

      {!loading && (
        <>
          <div className="gallery-grid">
            {images.length === 0 && (
              <p className="gallery-empty">{t('products.imagesEmpty')}</p>
            )}
            {images.map((img, i) => (
              <div key={img.id} className="gallery-item" data-primary={img.isPrimary}>
                <img src={img.url} alt={`${i + 1}`} className="gallery-thumb" />
                <div className="gallery-item-controls">
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleReorder('up', i)}
                    disabled={disabled || i === 0}>{'↑'}</button>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleReorder('down', i)}
                    disabled={disabled || i === images.length - 1}>{'↓'}</button>
                  {!img.isPrimary && (
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => handleSetPrimary(img.id)}
                      disabled={disabled}>{t('products.imageMakePrimary')}</button>
                  )}
                  {img.isPrimary && <span className="gallery-primary-badge">{t('products.imagePrimary')}</span>}
                  <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDelete(img.id)}
                    disabled={disabled}>{'✕'}</button>
                </div>
              </div>
            ))}
          </div>

          <div className="form-field">
            <label htmlFor="galleryUpload">{t('products.imagesUpload')}</label>
            <input
              id="galleryUpload"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              disabled={disabled || uploading}
              onChange={e => handleUpload(e.target.files)}
            />
            {uploading && <div className="spinner" />}
          </div>
        </>
      )}
    </div>
  )
}